import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  FloppyDisk,
  ArrowsClockwise,
  ArrowCounterClockwise,
  Eye,
  EnvelopeOpen,
  Sparkle,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatDateTime } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [draft, setDraft] = useState({ subject: '', body: '', enabled: true, recipients_override: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const bodyRef = useRef(null);
  const subjectRef = useRef(null);

  const active = useMemo(
    () => templates.find((t) => t.key === activeKey) || null,
    [templates, activeKey],
  );

  const dirty = useMemo(() => {
    if (!active) return false;
    return draft.subject !== active.subject
      || draft.body !== active.body
      || (draft.enabled ?? true) !== (active.enabled ?? true)
      || (draft.recipients_override || '') !== ((active.recipients_override || []).join(', '));
  }, [active, draft]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email-templates`);
      const items = res.data || [];
      setTemplates(items);
      if (items.length && !activeKey) {
        setActiveKey(items[0].key);
        setDraft({ subject: items[0].subject, body: items[0].body, enabled: items[0].enabled ?? true, recipients_override: (items[0].recipients_override || []).join(', ') });
      }
    } catch (_e) {
      toast.error('テンプレートの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = (key) => {
    if (dirty && !window.confirm('未保存の変更があります。破棄して切り替えますか？')) return;
    const t = templates.find((x) => x.key === key);
    if (!t) return;
    setActiveKey(key);
    setDraft({ subject: t.subject, body: t.body, enabled: t.enabled ?? true, recipients_override: (t.recipients_override || []).join(', ') });
  };

  const save = async () => {
    if (!active) return;
    if (!draft.subject.trim() || !draft.body.trim()) {
      toast.error('件名と本文は必須です');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/email-templates/${active.key}`, {
        subject: draft.subject,
        body: draft.body,
        enabled: draft.enabled,
        recipients_override: (draft.recipients_override || '').split(/[,;\n]/).map((s) => s.trim()).filter((s) => s.includes('@')),
      });
      setTemplates((prev) => prev.map((t) => (t.key === active.key ? { ...res.data, sender_actual: t.sender_actual } : t)));
      setDraft((d) => ({ ...d, recipients_override: (res.data.recipients_override || []).join(', ') }));
      toast.success('テンプレートを保存しました');
    } catch (e) {
      const msg = e.response?.data?.detail || '保存に失敗しました';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!active) return;
    if (!window.confirm('既定のテンプレートに戻しますか？編集内容は失われます。')) return;
    setResetting(true);
    try {
      const res = await axios.post(`${API}/email-templates/${active.key}/reset`);
      setTemplates((prev) => prev.map((t) => (t.key === active.key ? { ...res.data, sender_actual: t.sender_actual } : t)));
      setDraft({ subject: res.data.subject, body: res.data.body, enabled: res.data.enabled ?? true, recipients_override: (res.data.recipients_override || []).join(', ') });
      toast.success('既定のテンプレートに戻しました');
    } catch (_e) {
      toast.error('初期化に失敗しました');
    } finally {
      setResetting(false);
    }
  };

  // 自動送信 ON/OFF の即時保存 — 件名・本文の編集状態に関係なくその場で永続化する。
  // これまでスイッチだけ切り替えて「保存」を押し忘れ、翌朝リマインダーが送信される事故があった。
  const toggleEnabled = async (nextEnabled) => {
    if (!active) return;
    // 楽観更新
    setDraft((d) => ({ ...d, enabled: nextEnabled }));
    try {
      const res = await axios.patch(`${API}/email-templates/${active.key}/enabled`, { enabled: nextEnabled });
      setTemplates((prev) => prev.map((t) => (t.key === active.key ? { ...res.data, sender_actual: t.sender_actual } : t)));
      toast.success(nextEnabled ? '自動送信を有効にしました' : '自動送信を無効にしました');
    } catch (e) {
      // 失敗時はスイッチを元に戻す
      setDraft((d) => ({ ...d, enabled: !nextEnabled }));
      toast.error(e?.response?.data?.detail || '切り替えに失敗しました');
    }
  };

  const runPreview = async () => {
    if (!active) return;
    setPreviewing(true);
    setPreviewOpen(true);
    try {
      const res = await axios.post(`${API}/email-templates/${active.key}/preview`, {
        subject: draft.subject,
        body: draft.body,
      });
      setPreview(res.data);
    } catch (e) {
      const msg = e.response?.data?.detail || 'プレビューに失敗しました';
      toast.error(msg);
      setPreviewOpen(false);
    } finally {
      setPreviewing(false);
    }
  };

  const insertVariable = (varName) => {
    const token = `{{ ${varName} }}`;
    // Insert into the focused field (body by default)
    const el = bodyRef.current && document.activeElement === bodyRef.current
      ? bodyRef.current
      : (subjectRef.current && document.activeElement === subjectRef.current
        ? subjectRef.current
        : bodyRef.current);
    if (!el) return;
    const isSubject = el === subjectRef.current;
    const currentValue = isSubject ? draft.subject : draft.body;
    const start = el.selectionStart ?? currentValue.length;
    const end = el.selectionEnd ?? currentValue.length;
    const next = currentValue.slice(0, start) + token + currentValue.slice(end);
    if (isSubject) setDraft((d) => ({ ...d, subject: next }));
    else setDraft((d) => ({ ...d, body: next }));
    // Restore caret to after the inserted token
    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      } catch (_e) { /* ignore */ }
    });
  };

  return (
    <div className="space-y-6" data-testid="email-templates-page">
      {/* Header */}
      <header>
        <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-zinc-400">設定</p>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 mt-1">メールテンプレート</h1>
        <p className="text-sm text-zinc-500 mt-1">
          自動送信される各メールの件名・本文を編集できます。
          <span className="font-mono text-[12px] bg-zinc-100 px-1.5 py-0.5 rounded mx-1">{`{{ 変数名 }}`}</span>
          で動的な値を挿入します。
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Template list */}
        <aside className="space-y-2" data-testid="email-templates-list">
          {loading && templates.length === 0 ? (
            <p className="text-sm text-zinc-500 px-3 py-2">読み込み中…</p>
          ) : (
            templates.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => handleSelect(t.key)}
                data-testid={`tpl-tab-${t.key}`}
                className={`w-full text-left p-3 rounded-lg border transition-all ${
                  activeKey === t.key
                    ? 'bg-teal-700/5 border-teal-700/30 ring-1 ring-teal-700/20'
                    : 'bg-white border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <EnvelopeOpen size={16} weight="duotone" className={activeKey === t.key ? 'text-teal-700' : 'text-zinc-500'} />
                  <span className={`text-sm font-semibold ${activeKey === t.key ? 'text-teal-700' : 'text-zinc-900'}`}>
                    {t.label}
                  </span>
                  {t.toggleable && (t.enabled ?? true) === false && (
                    <Badge variant="secondary" className="ml-auto bg-zinc-200 text-zinc-600 text-[10px] px-1.5 py-0" data-testid={`tpl-badge-off-${t.key}`}>
                      無効
                    </Badge>
                  )}
                </div>
                <p className="text-[11px] text-zinc-500 line-clamp-2">{t.description}</p>
              </button>
            ))
          )}
        </aside>

        {/* Editor */}
        <section className="bg-white rounded-xl border border-zinc-200 p-6 space-y-5" data-testid="email-templates-editor">
          {!active ? (
            <p className="text-sm text-zinc-500 py-12 text-center">テンプレートを選択してください</p>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-zinc-900">{active.label}</h2>
                  <p className="text-xs text-zinc-500 mt-0.5">{active.description}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                  <Button variant="outline" size="sm" onClick={runPreview} disabled={previewing} data-testid="tpl-preview">
                    <Eye size={14} className="mr-1" /> プレビュー
                  </Button>
                  <Button variant="outline" size="sm" onClick={resetToDefault} disabled={resetting} data-testid="tpl-reset">
                    <ArrowCounterClockwise size={14} className="mr-1" /> 既定に戻す
                  </Button>
                  <Button size="sm" onClick={save} disabled={!dirty || saving} data-testid="tpl-save">
                    <FloppyDisk size={14} className="mr-1" weight="duotone" /> {saving ? '保存中…' : '保存'}
                  </Button>
                </div>
              </div>

              {/* ON/OFF toggle + 送信先 / 差出人 review */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {active.toggleable && (
                  <div
                    className={`rounded-lg border p-3 flex items-center justify-between gap-3 ${
                      draft.enabled ? 'bg-emerald-50/60 border-emerald-200' : 'bg-zinc-50 border-zinc-200'
                    }`}
                    data-testid="tpl-enabled-card"
                  >
                    <div>
                      <p className="text-xs font-semibold text-zinc-700">自動送信</p>
                      <p className={`text-[11px] mt-0.5 ${draft.enabled ? 'text-emerald-600' : 'text-zinc-500'}`}>
                        {draft.enabled ? '有効 — 送信されます' : '無効 — 送信されません'}
                      </p>
                      <p className="text-[10px] text-zinc-400 mt-0.5">切替はその場で保存されます</p>
                    </div>
                    <Switch
                      checked={!!draft.enabled}
                      onCheckedChange={toggleEnabled}
                      data-testid="tpl-enabled-switch"
                    />
                  </div>
                )}
                <div className="rounded-lg border border-zinc-200 bg-white p-3" data-testid="tpl-recipients">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">送信先（宛先）</p>
                  <p className="text-xs text-zinc-700 mt-1 leading-relaxed">{active.recipients_desc || '—'}</p>
                  {!['chat_mention', 'test_email'].includes(active.key) && (
                    <div className="mt-2 pt-2 border-t border-zinc-200">
                      <p className="text-[10px] font-semibold text-zinc-500">宛先の上書き（任意）</p>
                      <Input
                        value={draft.recipients_override || ''}
                        onChange={(e) => setDraft((d) => ({ ...d, recipients_override: e.target.value }))}
                        placeholder="a@example.jp, b@example.jp"
                        className="h-7 mt-1 text-[11px] font-mono"
                        data-testid="tpl-recipients-override"
                      />
                      <p className="text-[10px] text-zinc-400 mt-1">
                        {draft.recipients_override?.trim()
                          ? '⚠ この通知は上記の宛先のみに送信されます'
                          : '空欄 = 既定の宛先に送信。カンマ区切りで指定すると宛先を固定できます'}
                      </p>
                    </div>
                  )}
                </div>
                <div className="rounded-lg border border-zinc-200 bg-white p-3" data-testid="tpl-sender">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">差出人（送信元）</p>
                  <p className="text-xs text-zinc-700 mt-1 font-mono break-all">{active.sender_actual || '—'}</p>
                  <p className="text-[10px] text-zinc-400 mt-1">{active.sender_desc}</p>
                </div>
              </div>

              {/* Subject */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-zinc-700">件名</Label>
                <Input
                  ref={subjectRef}
                  value={draft.subject}
                  onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
                  className="font-mono text-sm"
                  data-testid="tpl-subject"
                  placeholder="件名を入力"
                />
              </div>

              {/* Body */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-zinc-700">本文</Label>
                <Textarea
                  ref={bodyRef}
                  value={draft.body}
                  onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
                  className="font-mono text-sm min-h-[280px] leading-relaxed"
                  data-testid="tpl-body"
                  placeholder="本文を入力"
                  spellCheck={false}
                />
                <p className="text-[11px] text-zinc-500">
                  改行はそのままメールの改行として送信されます。HTML を直接記述することもできます。
                </p>
              </div>

              {/* Variables */}
              <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <Sparkle size={14} className="text-amber-500" weight="fill" />
                  <span className="text-xs font-semibold text-zinc-700">利用可能な変数</span>
                  <span className="text-[11px] text-zinc-400">クリックで挿入</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(active.variables || []).map((v) => (
                    <button
                      key={v.name}
                      type="button"
                      onClick={() => insertVariable(v.name)}
                      data-testid={`tpl-var-${v.name}`}
                      className="group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white border border-zinc-200 hover:border-teal-700 hover:bg-teal-700/5 transition-colors"
                    >
                      <code className="text-[11px] text-teal-700 font-mono group-hover:underline">{`{{ ${v.name} }}`}</code>
                      <span className="text-[11px] text-zinc-500">{v.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {active.updated_at && (
                <p className="text-[11px] text-zinc-400">
                  最終更新: {formatDateTime(active.updated_at)}
                </p>
              )}
            </>
          )}
        </section>
      </div>

      {/* Preview modal */}
      <Dialog open={previewOpen} onOpenChange={(open) => { setPreviewOpen(open); if (!open) setPreview(null); }}>
        <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden" data-testid="tpl-preview-modal">
          <DialogHeader className="px-6 py-4 border-b border-zinc-200 bg-zinc-50/60">
            <DialogTitle className="text-base font-semibold text-zinc-900">
              プレビュー — {active?.label}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500 mt-1">
              サンプル値で実際の送信時のレンダリング結果を確認できます。
            </DialogDescription>
          </DialogHeader>
          <div className="bg-white">
            {previewing || !preview ? (
              <div className="h-[50vh] flex items-center justify-center text-sm text-zinc-500">
                <ArrowsClockwise size={20} className="animate-spin mr-2" /> 生成中…
              </div>
            ) : (
              <>
                <div className="px-6 py-3 border-b border-zinc-200 bg-zinc-50/60">
                  <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold">件名</p>
                  <p className="text-sm font-semibold text-zinc-900 mt-0.5 break-all">{preview.subject}</p>
                </div>
                {preview.body_html ? (
                  <iframe
                    title="email-template-preview"
                    srcDoc={preview.body_html}
                    sandbox=""
                    className="w-full h-[55vh] border-0 bg-white"
                    data-testid="tpl-preview-iframe"
                  />
                ) : (
                  <p className="px-6 py-8 text-sm text-zinc-500">本文がありません</p>
                )}
              </>
            )}
          </div>
          <div className="px-6 py-3 border-t border-zinc-200 bg-zinc-50/60 flex justify-end">
            <Button size="sm" onClick={() => setPreviewOpen(false)} data-testid="tpl-preview-close">
              閉じる
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
